// PartnerDetail.jsx avec affichage des individuals liés

import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AddPositionForm from '../../components/position/AddPositionForm';
import EditPartnerForm from '../../components/partners/EditPartnerForm';
import { ApiContext } from '../../App';
import AddIndividualToPartner from "../../components/partners/AddIndividualToPartner";
import ContractUploader from '../../components/partners/ContractUploader';
import { DynamicBreadcrumb } from '../../components/utils/Breadcrumb';
import { toast } from 'react-toastify';
import ConfirmationDialog from '../../components/utils/ConfirmationDialog';
import { getFirstEmail } from '../../utils/emailHelpers';

const PartnerDetail = () => {
  const API = useContext(ApiContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [partner, setPartner] = useState(null);
  const [relatedIndividuals, setRelatedIndividuals] = useState([]); // Nouveau state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showPositionForm, setShowPositionForm] = useState(false);
  const [isUploadingContract, setIsUploadingContract] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingIndividuals, setLoadingIndividuals] = useState(true); // Loading séparé
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteContractDialog, setShowDeleteContractDialog] = useState(false);

  useEffect(() => {
    const fetchPartner = async () => {
      setLoading(true);
      try {
        const response = await API.get(`/api/partners/${id}`);
        setPartner(response.data);
        
        // Récupérer les individuals liés après avoir récupéré le partner
        await fetchRelatedIndividuals(response.data.name);
      } catch (error) {
        console.error('Error fetching partner:', error);
        toast.error('Erreur lors du chargement des détails du partenaire');
      } finally {
        setLoading(false);
      }
    };

    fetchPartner();
  }, [id, API]);

  // Fonction pour récupérer les individuals liés à cette entreprise
  const fetchRelatedIndividuals = async (companyName) => {
    if (!companyName) return;
    
    setLoadingIndividuals(true);
    try {
      const response = await API.get('/api/individuals');
      // Filtrer les individuals qui ont cette entreprise
      const filtered = response.data.filter(individual => 
        individual.company && 
        individual.company.toLowerCase() === companyName.toLowerCase()
      );
      setRelatedIndividuals(filtered);
    } catch (error) {
      console.error('Error fetching related individuals:', error);
    } finally {
      setLoadingIndividuals(false);
    }
  };

  const handleEditSuccess = (updatedPartner) => {
    setPartner(updatedPartner);
    setIsEditModalOpen(false);
    
    // Rafraîchir les individuals si le nom de l'entreprise a changé
    fetchRelatedIndividuals(updatedPartner.name);
  };

  const openEditModal = () => {
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleContractUpload = (newContractPath) => {
    setPartner(prevPartner => ({
      ...prevPartner,
      contractFile: newContractPath,
    }));
    setIsUploadingContract(false);
  };

  const handleDeleteContract = async () => {
    try {
      await API.delete(`/api/partners/${id}/contract`);
      setPartner(prevPartner => ({
        ...prevPartner,
        contractFile: null,
      }));
      setShowDeleteContractDialog(false);
      toast.success('Contrat supprimé avec succès');
    } catch (error) {
      console.error('Error removing contract:', error);
      toast.error('Erreur lors de la suppression du contrat');
    }
  };

  const handleToggleContractUpload = () => {
    setIsUploadingContract(!isUploadingContract);
  };

  const handleDeletePartner = async () => {
    try {
      await API.delete(`/api/partners/${id}`);
      toast.success('Entreprise supprimée avec succès');
      navigate('/partners');
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error('Erreur lors de la suppression du partenaire');
    }
  };

  // Fonction pour naviguer vers le détail de l'individual
  const handleOpenIndividualSidebar = (individualId) => {
    navigate(`/partners/${id}/contacts/${individualId}`);
  };

  // Helper function to get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'cold': return 'bg-gray-100 text-gray-700';
      case 'prospect': return 'bg-yellow-100 text-yellow-700';
      case 'client': return 'bg-green-100 text-green-700';
      case 'old client': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 pt-16">
        <div className="max-w-7xl mx-auto p-6 lg:p-10">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 pt-16">
        <div className="max-w-7xl mx-auto p-6 lg:p-10">
          <div className="text-center py-20">
            <i className="bi bi-exclamation-triangle text-4xl text-red-500 mb-4"></i>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Partenaire non trouvé</h2>
            <p className="text-slate-600 mb-6">Le partenaire que vous recherchez n'existe pas ou a été supprimé.</p>
            <Link
              to="/partners"
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <i className="bi bi-arrow-left"></i>
              Retour aux partenaires
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-10">
      <div className=" mx-auto p-4 sm:p-6 lg:p-10">
        {/* Breadcrumb */}
        <DynamicBreadcrumb 
          baseItems={[
            { label: 'Dashboard', href: '/app', icon: 'bi-house' },
            { label: 'Entreprises', href: '/partners', icon: 'bi-building' }
          ]} 
          currentItem={{ name: partner.name }}
        />

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Partner Info */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Header Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 sm:mb-6">
          {/* Hero Banner */}
          <div className="p-4 sm:p-6">
            <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4 sm:gap-6">
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-3">
                  <div className='flex items-center gap-2'>
                      <div className=' '>
                        {partner.photo && (
                            <img src={partner.photo} alt="Logo Enterprise" className='w-12 h-12  bg-white rounded-lg ' />
                        )}
                      </div>
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight m-0">{partner.name}</h1>
                  </div>

                  {/* Status Badge - Properly aligned */}
                  {partner.status && (
                    <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getStatusBadgeColor(partner.status)}`}>
                      {partner.status}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-700">
                  <div className="flex items-center space-x-2 text-sm sm:text-base">
                    <i className="bi bi-geo-alt-fill text-slate-500"></i>
                    <span>{partner.city || 'Aucune localisation spécifiée'}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm sm:text-base">
                    <i className="bi bi-briefcase-fill text-slate-500"></i>
                    <span>
                      <span className="font-semibold text-emerald-600">{partner.openPositions.length}</span> postes ouverts
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm sm:text-base">
                    <i className="bi bi-people-fill text-slate-500"></i>
                    <span>
                      <span className="font-semibold text-indigo-600">{relatedIndividuals.length}</span> contacts
                    </span>
                  </div>
                  {partner.employeeCount && (
                    <div className="flex items-center space-x-2 text-sm sm:text-base">
                      <i className="bi bi-building text-slate-500"></i>
                      <span>
                        <span className="font-semibold text-slate-700">{partner.employeeCount}</span> employés
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row xl:flex-col xl:w-auto items-stretch sm:items-center xl:items-stretch gap-2 sm:gap-3">
                <button
                  onClick={() => setShowDeleteDialog(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100
                           text-red-600 rounded-lg transition-colors text-sm font-medium border-1 border-red-200"
                >
                  <i className="bi bi-trash"></i>
                  <span>Supprimer</span>
                </button>
                <button
                  onClick={openEditModal}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                           text-white rounded-lg transition-colors shadow-sm text-sm font-medium"
                >
                  <i className="bi bi-pencil-square"></i>
                  <span>Modifier</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {partner.description && partner.description.trim().length > 0 && (
          <>
           {/* Description */}
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="flex bg-slate-100 rounded-t-xl items-center gap-3 border-b py-3 px-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 m-0">Description</h3>
                </div>
              </div>

              <div className="rounded-lg py-3 px-4">
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                  {partner.description}
                </p>
              </div>
            </div>
          </>
        )

        }



      {/* Related Individuals Section */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="flex items-center gap-3 py-3 px-4 rounded-t-xl border-b">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 m-0">
              Contacts
              {relatedIndividuals.length > 0 && (
                <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full font-medium">
                  {relatedIndividuals.length}
                </span>
              )}
            </h3>
          </div>
        </div>

        {loadingIndividuals ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          </div>
        ) : relatedIndividuals.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-slate-200 mx-4 my-4">
            <i className="bi bi-person-plus text-4xl text-slate-400 mb-4"></i>
            <h4 className="text-lg font-medium text-slate-600 mb-2">Aucun contact trouvé</h4>
            <p className="text-slate-500 mb-4">
              Aucun individu de votre base de données n'est associé à {partner.name} pour le moment.
            </p>
            <Link
              to="/all-individuals"
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              <i className="bi bi-plus-lg"></i>
              Ajouter des individus
            </Link>
          </div>
        ) : (
           <div className="max-h-64 overflow-x-auto overflow-y-auto ">
    <div className="min-w-full  border-slate-200 rounded-lg divide-y divide-slate-100">
      {relatedIndividuals.map((individual) => (
        <div
          key={individual._id}
          onClick={() => handleOpenIndividualSidebar(individual._id)}
          className="grid grid-cols-1 sm:grid-cols-6 gap-2 sm:gap-4 items-center px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
        >
          {/* Col 1 - Nom + photo */}
          <div className="flex items-center gap-2 col-span-1 sm:col-span-2">
            {individual.photo ? (
              <img
                src={individual.photo}
                alt={individual.Name}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 bg-slate-300 flex items-center justify-center rounded-full text-white text-sm flex-shrink-0">
                {individual.Name?.charAt(0)?.toUpperCase() || 'N'}
              </div>
            )}
            <span className="text-sm font-medium text-slate-900 truncate">{individual.Name}</span>
            {individual.score && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex-shrink-0">
                <i className="bi bi-star-fill text-xs"></i>
                {individual.score}/5
              </span>
            )}
          </div>

          {/* Col 2 - Poste */}
          <div className="text-sm text-slate-700 truncate hidden sm:block">
            {individual.jobTitle || <span className="text-slate-400 italic">—</span>}
          </div>

          {/* Col 3 - Email */}
          <div className="text-sm text-slate-700 truncate hidden sm:block">
            {getFirstEmail(individual.professionalEmail) || getFirstEmail(individual.personalEmail) || (
              <span className="text-slate-400 italic">—</span>
            )}
          </div>

          {/* Col 4 - Téléphone */}
          <div className="text-sm text-slate-700 truncate hidden sm:block">
            {individual.phoneNumber && individual.phoneNumber !== 'Téléphone indisponible'
              ? individual.phoneNumber
              : <span className="text-slate-400 italic">—</span>}
          </div>

        </div>
      ))}
      </div>
      </div>
    )}     
    </div>




            

            {/* 
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <AddIndividualToPartner partnerId={id} />
            </div>
            */}

            {/* Open Positions */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <i className="bi bi-briefcase text-emerald-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 m-0">
                      Postes
                      {partner.openPositions.length > 0 && (
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                        </span>
                      )}
                    </h3>
                    <p className="text-sm sm:text-base text-slate-600">{partner.openPositions.length} postes</p>
                  </div>
                </div>
                
                <button
                  onClick={() => setShowPositionForm(!showPositionForm)}
                  className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                    showPositionForm
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  }`}
                >
                  <i className={`bi ${showPositionForm ? 'bi-x-lg' : 'bi-plus-lg'}`}></i>
                  <span className="hidden sm:inline">{showPositionForm ? 'Annuler' : 'Ajouter un poste'}</span>
                  <span className="sm:hidden">{showPositionForm ? 'Annuler' : 'Ajouter'}</span>
                </button>
              </div>

              {showPositionForm && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-slate-200">
                  <AddPositionForm />
                </div>
              )}

              {/* Positions Grid */}
              {partner.openPositions.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-slate-200">
                  <i className="bi bi-briefcase text-4xl text-slate-400 mb-4"></i>
                  <h4 className="text-lg font-medium text-slate-600 mb-2">Aucun poste ouvert</h4>
                  <p className="text-slate-500 mb-4">Ce partenaire n'a pas encore d'offres d'emploi actives.</p>
                  <button
                    onClick={() => setShowPositionForm(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <i className="bi bi-plus-lg"></i>
                    Ajouter le premier poste
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {partner.openPositions.map((position) => (
                    <div 
                      key={position._id} 
                      className="group bg-gray-50 hover:bg-white border border-slate-200 hover:border-slate-300 
                               rounded-lg p-4 transition-all hover:shadow-md"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <Link
                          to={`/positions/${position._id}`}
                          className="text-lg font-semibold text-slate-900 hover:text-indigo-600 
                                   transition-colors group-hover:underline"
                        >
                          {position.title}
                        </Link>
                        <i className="bi bi-arrow-up-right text-slate-400 group-hover:text-indigo-600 transition-colors"></i>
                      </div>
                      
                      <div className="space-y-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <i className="bi bi-geo-alt text-xs"></i>
                          <span>{position.location || 'À distance'}</span>
                        </div>
                        
                        {position.salary && (
                          <div className="flex items-center gap-2">
                            <i className="bi bi-currency-euro text-xs"></i>
                            <span>{position.salary}</span>
                          </div>
                        )}
                        
                        {position.contractType && (
                          <div className="flex items-center gap-2">
                            <i className="bi bi-file-earmark text-xs"></i>
                            <span>{position.contractType}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Partner Info Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <i className="bi bi-info-circle text-slate-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 m-0">Informations partenaire</h3>
                  <p className="text-sm sm:text-base text-slate-600">Détails clés et statut</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Employee Count */}
                {partner.employeeCount && (
                  <div className="flex items-center justify-between py-3 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <i className="bi bi-people text-slate-500"></i>
                      <span className="text-sm font-medium text-slate-700">Employés</span>
                    </div>
                    <span className="text-sm text-slate-900 font-medium">
                      {partner.employeeCount}
                    </span>
                  </div>
                )}

                {/* LinkedIn URL */}
                {partner.linkedinUrl && (
                  <div className="flex items-center justify-between py-3 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <i className="bi bi-linkedin text-slate-500"></i>
                      <span className="text-sm font-medium text-slate-700">LinkedIn</span>
                    </div>
                    <a
                      href={partner.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Voir le profil
                    </a>
                  </div>
                )}

                {/* Internal Manager */}
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <i className="bi bi-person text-slate-500"></i>
                    <span className="text-sm font-medium text-slate-700">Responsable interne</span>
                  </div>
                  <span className="text-sm text-slate-900 font-medium">
                    {partner.internalManager || 'Non assigné'}
                  </span>
                </div>

                {/* Contract Status */}
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <i className="bi bi-file-earmark-check text-slate-500"></i>
                    <span className="text-sm font-medium text-slate-700">Statut du contrat</span>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    partner.contractSigned
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    <i className={`bi ${partner.contractSigned ? 'bi-check-circle-fill' : 'bi-clock-fill'}`}></i>
                    {partner.contractSigned ? 'Signé' : 'En attente'}
                  </span>
                </div>

                {/* Contacts */}
                {partner.contacts && partner.contacts.length > 0 && (
                  <div className="py-3">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="bi bi-people text-slate-500"></i>
                      <span className="text-sm font-medium text-slate-700">Contacts clés</span>
                    </div>
                    <div className="space-y-1">
                      {partner.contacts.map((contact, index) => (
                        <span 
                          key={index} 
                          className="inline-block bg-gray-100 text-slate-700 px-2 py-1 rounded text-xs mr-1 mb-1"
                        >
                          {contact}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Technologies */}
            {partner.technologies && partner.technologies.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="bi bi-code-slash text-purple-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900 m-0">Tech Stack</h3>
                    <p className="text-sm sm:text-base text-slate-600">Tech stack utilisée par l'entreprise</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {partner.technologies.map((tech, index) => (
                    <span 
                      key={index} 
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {partner.tags && partner.tags.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="bi bi-tags text-orange-600 text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900 m-0">Tags</h3>
                    <p className="text-sm sm:text-base text-slate-600">Catégories et étiquettes du partenaire</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {partner.tags.map((tag, index) => (
                    <span 
                      key={index} 
                      className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Contract Management */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="bi bi-file-earmark-pdf text-purple-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 m-0">Contrat</h3>
                  <p className="text-sm sm:text-base text-slate-600">Gérer l'accord de partenariat</p>
                </div>
              </div>

              {partner.contractFile ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <i className="bi bi-file-earmark-pdf text-emerald-600 text-xl"></i>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-emerald-800">Contrat disponible</p>
                      <p className="text-xs text-emerald-600">Accord de partenariat téléchargé</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${partner.contractFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2
                               bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                    >
                      <i className="bi bi-eye"></i>
                      Voir le contrat
                    </a>

                    <button
                      onClick={handleToggleContractUpload}
                      className="px-3 py-2 bg-gray-100 text-slate-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="Remplacer le contrat"
                    >
                      <i className="bi bi-pencil"></i>
                    </button>

                    <button
                      onClick={() => setShowDeleteContractDialog(true)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      title="Supprimer le contrat"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <i className="bi bi-cloud-upload text-2xl text-slate-400"></i>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">Aucun contrat téléchargé</p>
                  <button
                    onClick={() => setIsUploadingContract(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2
                             bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <i className="bi bi-upload"></i>
                    Télécharger le contrat
                  </button>
                </div>
              )}

              {isUploadingContract && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-slate-200">
                  <ContractUploader 
                    partnerId={id} 
                    onContractUpload={handleContractUpload}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Edit Partner Modal */}
        <EditPartnerForm
          partner={partner}
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          onEditSuccess={handleEditSuccess}
        />

        {/* Confirmation Dialog for Partner Deletion */}
        <ConfirmationDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDeletePartner}
          title="Supprimer le partenaire"
          message={`Êtes-vous sûr de vouloir supprimer le partenaire "${partner.name}" ? Cette action est irréversible et supprimera également tous les postes et données associés.`}
          confirmText="Supprimer"
          cancelText="Annuler"
          isDangerous={true}
        />

        {/* Confirmation Dialog for Contract Deletion */}
        <ConfirmationDialog
          isOpen={showDeleteContractDialog}
          onClose={() => setShowDeleteContractDialog(false)}
          onConfirm={handleDeleteContract}
          title="Supprimer le contrat"
          message="Êtes-vous sûr de vouloir supprimer ce contrat ? Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          isDangerous={true}
        />
      </div>
    </div>
  );
};

export default PartnerDetail;